import { Navigate, type RouteObject } from 'react-router-dom';

import { AdminATSAuthenticatedLayout } from './AdminATSAuthenticatedLayout';
import { AdminAuthenticatedLayout } from './AdminAuthenticatedLayout';
import { AdminPublicOnlyLayout } from './AdminPublicOnlyLayout';
import { AdminRootLayout } from './AdminRootLayout';
import { AdminSettingsAccountLayout } from './AdminSettingsAccountLayout';
import { AdminSettingsCommunicationLayout } from './AdminSettingsCommunicationLayout';
import { AdminSettingsLayout } from './AdminSettingsLayout';
import { AdminSettingsSettingsLayout } from './AdminSettingsSettingsLayout';
import { AdminSettingsSharingLayout } from './AdminSettingsSharingLayout';
import { AdminSynapsAdminAuthenticatedLayout } from './AdminSynapsAdminAuthenticatedLayout';

export const adminRoutes: RouteObject = {
  path: 'admin',
  element: <AdminRootLayout />,
  children: [
    { index: true, element: <Navigate to="dashboard" replace /> },
    {
      path: '',
      element: <AdminAuthenticatedLayout />,
      children: [
        {
          path: 'dashboard',
          lazy: () => import('../../app/admin/(authenticated)/dashboard/page'),
        },
        {
          path: 'interviews/new',
          lazy: () =>
            import('../../app/admin/(authenticated)/interviews/new/page'),
        },
        {
          path: 'surveys',
          children: [
            {
              index: true,
              lazy: () =>
                import('../../app/admin/(authenticated)/surveys/page'),
            },
            {
              path: 'new',
              lazy: () =>
                import('../../app/admin/(authenticated)/surveys/new/page'),
            },
            {
              path: ':surveyId',
              lazy: () =>
                import(
                  '../../app/admin/(authenticated)/surveys/[surveyId]/page'
                ),
            },
          ],
        },
        {
          path: 'interviews',
          children: [
            {
              index: true,
              lazy: () =>
                import('../../app/admin/(authenticated)/interviews/page'),
            },
            {
              path: ':interviewId/candidates/:candidateId',
              lazy: () =>
                import('../../app/admin/(authenticated)/candidate/page'),
            },
            {
              path: ':interviewId',
              lazy: () =>
                import('../../app/admin/(authenticated)/interview/page'),
            },
            {
              path: ':interviewId/compare',
              lazy: () =>
                import(
                  '../../app/admin/(authenticated)/interviews/compare/page'
                ),
            },
            {
              path: ':interviewId/report',
              lazy: () =>
                import(
                  '../../app/admin/(authenticated)/interviews/report/page'
                ),
            },
            {
              path: ':interviewId/shared-report/:reportId',
              element: null,
              lazy: () =>
                import(
                  '../../app/admin/(authenticated)/interviews/report/share/page'
                ),
            },
          ],
        },
        {
          path: 'candidates',
          children: [
            {
              index: true,
              lazy: () =>
                import('../../app/admin/(authenticated)/candidates/page'),
            },
          ],
        },
      ],
    },
    {
      path: '',
      element: <AdminATSAuthenticatedLayout />,
      children: [
        {
          path: 'boards',
          lazy: () => import('../../app/admin/(authenticated)/boards/page'),
        },
        {
          path: 'boards/positions',
          lazy: () =>
            import('../../app/admin/(authenticated)/boards/positions/page'),
        },
        {
          path: 'boards/candidates',
          lazy: () =>
            import('../../app/admin/(authenticated)/boards/candidates/page'),
        },
        {
          path: 'boards/settings',
          lazy: () =>
            import('../../app/admin/(authenticated)/boards/settings/page'),
        },
        {
          path: 'boards/reports',
          lazy: () =>
            import('../../app/admin/(authenticated)/boards/reports/page'),
        },
        {
          path: 'boards/:boardId',
          lazy: () => import('../../app/admin/(authenticated)/board/page'),
        },
      ],
    },
    {
      path: 'settings',
      element: <AdminAuthenticatedLayout />,
      children: [
        {
          path: '',
          element: <AdminSettingsLayout />,
          children: [
            { index: true, element: <Navigate to="account" replace /> },

            {
              path: 'account',
              element: <AdminSettingsAccountLayout />,
              children: [
                { index: true, element: <Navigate to="team" replace /> },
                {
                  path: 'team',
                  lazy: () =>
                    import(
                      '../../app/admin/(authenticated)/settings/account/team/page'
                    ),
                },
                {
                  path: 'company',
                  lazy: () =>
                    import(
                      '../../app/admin/(authenticated)/settings/account/company/page'
                    ),
                },
              ],
            },
            {
              path: '',
              element: <AdminSettingsSettingsLayout />,
              children: [
                {
                  index: true,
                  element: <Navigate to="communication" replace />,
                },

                {
                  path: 'communication',
                  element: <AdminSettingsCommunicationLayout />,
                  children: [
                    { index: true, element: <Navigate to="emails" replace /> },
                    {
                      path: 'emails',
                      lazy: () =>
                        import(
                          '../../app/admin/(authenticated)/settings/communication/emails/page'
                        ),
                    },
                    {
                      path: 'sms',
                      lazy: () =>
                        import(
                          '../../app/admin/(authenticated)/settings/communication/sms/page'
                        ),
                    },
                    {
                      path: 'reminder-call',
                      lazy: () =>
                        import(
                          '../../app/admin/(authenticated)/settings/communication/reminder-call/page'
                        ),
                    },
                    {
                      path: 'whatsapp',
                      lazy: () =>
                        import(
                          '../../app/admin/(authenticated)/settings/communication/whatsapp/page'
                        ),
                    },
                  ],
                },
                {
                  path: 'sharing',
                  element: <AdminSettingsSharingLayout />,
                  children: [
                    {
                      index: true,
                      element: <Navigate to="sharing-history" replace />,
                    },
                    {
                      path: 'sharing-history',
                      lazy: () =>
                        import(
                          '../../app/admin/(authenticated)/settings/sharing/sharing-history/page'
                        ),
                    },
                  ],
                },
                {
                  path: 'integrations',
                  lazy: () =>
                    import(
                      '../../app/admin/(authenticated)/settings/integrations/page'
                    ),
                },
                {
                  path: 'plans',
                  lazy: () =>
                    import(
                      '../../app/admin/(authenticated)/settings/plans/page'
                    ),
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: '',
      element: <AdminSynapsAdminAuthenticatedLayout />,
      children: [
        {
          path: 'synaps-admin',
          lazy: () => import('../../app/admin/(synaps-admin)/page'),
        },
        {
          path: 'synaps-admin/coupons',
          children: [
            {
              index: true,
              lazy: () => import('../../app/admin/(synaps-admin)/coupons/page'),
            },
            {
              path: 'new',
              lazy: () =>
                import('../../app/admin/(synaps-admin)/coupons/new/page'),
            },
            {
              path: ':code',
              lazy: () =>
                import('../../app/admin/(synaps-admin)/coupons/[code]/page'),
            },
          ],
        },
        {
          path: 'synaps-admin/companies',
          children: [
            {
              index: true,
              lazy: () =>
                import('../../app/admin/(synaps-admin)/companies/page'),
            },
            {
              path: ':id',
              lazy: () =>
                import('../../app/admin/(synaps-admin)/companies/[id]/page'),
            },
          ],
        },
        {
          path: 'synaps-admin/prompts',
          children: [
            {
              index: true,
              lazy: () => import('../../app/admin/(synaps-admin)/prompts/page'),
            },
          ],
        },
      ],
    },
    {
      path: '',
      element: <AdminPublicOnlyLayout />,
      children: [
        {
          path: 'login',
          lazy: () => import('../../app/admin/(public-only)/login/page'),
        },
        {
          path: 'login/sso',
          lazy: () => import('../../app/admin/(public-only)/login/sso/page'),
        },
        {
          path: 'login/saml',
          lazy: () => import('../../app/admin/(public-only)/login/saml/page'),
        },
        {
          path: 'forgot-password',
          lazy: () =>
            import('../../app/admin/(public-only)/forgot-password/page'),
        },
        {
          path: 'reset-password',
          lazy: () =>
            import('../../app/admin/(public-only)/reset-password/page'),
        },
      ],
    },
  ],
};
